import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Card, Grid, Typography, CircularProgress, Alert, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveBar } from "@nivo/bar";

const COLORS = ["#355d31", "#22c55e", "#fbbc04", "#ec4899", "#06b6d4"];
const EMPTY_CHART_TEXT = "There is no Data";

const parseFiniteNumber = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (value === null || value === undefined) {
    return 0;
  }
  const normalized = String(value)
    .replace(/,/g, "")
    .replace(/[^0-9.-]/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toSafeAmount = (value) => Math.abs(parseFiniteNumber(value));

const toSafeCurrencyLabel = (value) => {
  const label = String(value || "").trim().toUpperCase();
  if (label === "AFN" || label === "AFG") return "AFG";
  if (label === "USD") return "USD";
  return label || "Unknown";
};

const normalizeChartCurrency = (value) => {
  const label = toSafeCurrencyLabel(value);
  return label && label !== "Unknown" ? label : "Unknown";
};

const buildBarSeries = (source = []) =>
  Object.fromEntries(
    (source || []).map((item) => [
      normalizeChartCurrency(item?.currency),
      toSafeAmount(item?.amount),
    ])
  );

const hasPositiveValue = (rows = [], keys = []) =>
  rows.some((row) => keys.some((key) => parseFiniteNumber(row?.[key]) > 0));

const formatDashboardAmount = (value, decimalPlaces = 2) => {
  const amount = parseFiniteNumber(value);
  if (!Number.isFinite(amount)) return Number(0).toLocaleString(undefined, { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces });
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
};

const buildPieChartData = (rows = []) =>
  (rows || [])
    .map((row) => {
      const label = normalizeChartCurrency(row?.currency || row?.currencyKey);
      const value = parseFiniteNumber(row?.amount ?? row?.totalAmount);
      return {
        id: label,
        label,
        value: Number.isFinite(value) ? value : 0,
      };
    })
    .filter((item) => item.label && Number.isFinite(item.value) && item.value > 0);

const buildComparisonChartData = (leftRows = [], rightRows = []) => {
  const map = new Map();

  const addRows = (rows = [], field) => {
    rows.forEach((row) => {
      const currency = normalizeChartCurrency(row?.currency || row?.currencyKey);
      const amount = parseFiniteNumber(row?.amount ?? row?.totalAmount);
      if (!Number.isFinite(amount)) return;
      const existing = map.get(currency) || { currency, sales: 0, purchases: 0, sales_paid: 0, purchases_paid: 0 };
      existing[field] += amount;
      map.set(currency, existing);
    });
  };

  addRows(leftRows, "sales");
  addRows(rightRows, "purchases");
  return Array.from(map.values()).filter((row) => row.sales > 0 || row.purchases > 0);
};

const buildPaymentComparisonChartData = (leftRows = [], rightRows = []) => {
  const map = new Map();

  const addRows = (rows = [], field) => {
    rows.forEach((row) => {
      const currency = normalizeChartCurrency(row?.currency || row?.currencyKey);
      const amount = parseFiniteNumber(row?.amount ?? row?.totalAmount);
      if (!Number.isFinite(amount)) return;
      const existing = map.get(currency) || { currency, sales_paid: 0, purchases_paid: 0 };
      existing[field] += amount;
      map.set(currency, existing);
    });
  };

  addRows(leftRows, "sales_paid");
  addRows(rightRows, "purchases_paid");
  return Array.from(map.values()).filter((row) => row.sales_paid > 0 || row.purchases_paid > 0);
};

const resolveCurrencyTitle = (currencies = [], value) => {
  const selected = currencies.find((option) => String(option?.id) === String(value));
  return String(selected?.title || selected?.code || selected?.name || "").trim();
};

const readAgentBillItemsForAgents = (agents = []) => {
  if (typeof window === "undefined") return [];

  const rows = [];
  const agentIdSet = new Set((agents || []).map((agent) => String(agent?.id)));

  (agents || []).forEach((agent) => {
    const billItems = Array.isArray(agent?.bill_items) ? agent.bill_items : [];
    billItems.forEach((item, index) => {
      rows.push({
        id: `${agent.id}-${item?.billNo || index}`,
        agentId: String(agent.id),
        agentType: String(agent?.agent_type || "").toLowerCase(),
        currency: item?.currency,
        total: parseFiniteNumber(item?.total),
        paid: parseFiniteNumber(item?.paid),
        remaining: parseFiniteNumber(item?.remaining ?? (parseFiniteNumber(item?.total) - parseFiniteNumber(item?.paid))),
        qty: parseFiniteNumber(item?.qty),
      });
    });
  });

  // Legacy fallback for data that has not yet been synced from browser storage.
  Object.keys(window.localStorage)
    .filter((key) => key.startsWith("agent-items-"))
    .forEach((key) => {
      const agentId = key.replace("agent-items-", "");
      if (!agentIdSet.has(String(agentId))) return;
      const agent = (agents || []).find((item) => String(item?.id) === String(agentId));
      if (agent && Array.isArray(agent.bill_items) && agent.bill_items.length > 0) return;

      let parsed = [];
      try {
        const raw = window.localStorage.getItem(key);
        const maybeData = JSON.parse(raw);
        parsed = Array.isArray(maybeData) ? maybeData : [];
      } catch {
        parsed = [];
      }

      parsed.forEach((item, index) => {
        const total = parseFiniteNumber(item?.total);
        const paid = parseFiniteNumber(item?.paid);
        rows.push({
          id: `${agentId}-${item?.billNo || index}`,
          agentId,
          agentType: String(agent?.agent_type || "").toLowerCase(),
          currency: item?.currency,
          total,
          paid,
          remaining: total - paid,
          qty: parseFiniteNumber(item?.qty),
        });
      });
    });

  return rows;
};

const Dashboard = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;
  const theme = useTheme();
  const isDarkMode = theme?.palette?.mode === "dark";
  const themeDirection = localStorage.getItem("direction");
  const isSuperAdmin = Boolean(user?.user?.is_superuser || user?.is_superuser);
  const panelBg = isDarkMode ? "#151515" : "#fff";
  const panelBorder = isDarkMode ? "1px solid rgba(255,255,255,0.22)" : "1px solid #d3ddcf";
  const panelHoverBorder = isDarkMode ? "rgba(255,255,255,0.34)" : "#9db595";
  const panelShadow = isDarkMode ? "0 12px 30px rgba(0,0,0,0.42)" : "0 6px 18px rgba(53,93,49,0.08)";
  const panelText = isDarkMode ? "#f3f4f6" : "#111827";
  const panelSubtext = isDarkMode ? "rgba(255,255,255,0.68)" : "#6b7280";

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const formattedStart = startDate?.format("YYYY-MM-DD") || "";
  const formattedEnd = endDate?.format("YYYY-MM-DD") || "";
  const [dateError, setDateError] = useState(null);
  const { data: agentsData, refetch: refetchAgents } = useFetchObjects(
    ["dashboard-agents", "global"],
    "shop/customers?page_size=all&scope=global",
    token
  );
  const { data: currenciesData, refetch: refetchCurrencies } = useFetchObjects(
    ["dashboard-currencies", "global"],
    "shop/currencies?page_size=all&scope=global",
    token
  );
  const agents = Array.isArray(agentsData) ? agentsData : agentsData?.results || [];
  const suppliers = agents.filter((agent) => String(agent?.agent_type || "").toLowerCase() === "supplier");
  const customers = agents.filter((agent) => String(agent?.agent_type || "").toLowerCase() === "customer");
  const dashboardQuery = `shop/financial-report/?scope=global&start_date=${formattedStart}&end_date=${formattedEnd}${selectedSupplier ? `&supplier=${selectedSupplier}` : ""}${selectedCustomer ? `&customer=${selectedCustomer}` : ""}`;

  useEffect(() => {
    if (startDate && endDate && endDate.isBefore(startDate)) {
      setDateError("Invalid date range: End date must be after start date.");
    } else {
      setDateError(null);
    }
  }, [startDate, endDate]);

  const { data, isLoading, isError, error, refetch: refetchDashboard } = useFetchObjects(
    ["dashboardReport", "global", formattedStart, formattedEnd, selectedSupplier, selectedCustomer],
    dashboardQuery,
    token
  );

  useEffect(() => {
    const refreshDashboardData = () => {
      refetchAgents?.();
      refetchCurrencies?.();
      refetchDashboard?.();
    };

    window.addEventListener("agent-items-updated", refreshDashboardData);
    return () => {
      window.removeEventListener("agent-items-updated", refreshDashboardData);
    };
  }, [refetchAgents, refetchCurrencies, refetchDashboard]);

  const pieDataFrom = (type) => {
    if (!data || !data[type] || !Array.isArray(data[type]) || data[type].length === 0) {
      return [];
    }
    return data[type]
      .map((item) => {
        const val = toSafeAmount(item?.amount);
        return {
          id: toSafeCurrencyLabel(item?.currency),
          label: toSafeCurrencyLabel(item?.currency),
          value: val,
        };
      })
      .filter((item) => Number.isFinite(item.value) && item.value > 0);
  };

  const currencies = Array.isArray(currenciesData)
    ? currenciesData
    : currenciesData?.results || [];

  const buildAgentSummary = (agentRows = []) => {
    const rows = readAgentBillItemsForAgents(agentRows);
    const currencyMap = new Map();

    rows.forEach((item) => {
      const currencyKey = String(item.currency ?? "").trim() || "unknown";
      const currencyTitle = normalizeChartCurrency(resolveCurrencyTitle(currencies, item.currency) || item.currency);
      if (!currencyMap.has(currencyKey)) {
        currencyMap.set(currencyKey, {
          currencyKey,
          currency: currencyTitle,
          totalQty: 0,
          totalAmount: 0,
          totalPaid: 0,
          totalRemaining: 0,
        });
      }

      const entry = currencyMap.get(currencyKey);
      entry.totalQty += parseFiniteNumber(item.qty);
      entry.totalAmount += parseFiniteNumber(item.total);
      entry.totalPaid += parseFiniteNumber(item.paid);
      entry.totalRemaining += Math.max(parseFiniteNumber(item.remaining), 0);
    });

    const currencyBreakdown = Array.from(currencyMap.values()).map((row) => ({
      ...row,
      currency: normalizeChartCurrency(resolveCurrencyTitle(currencies, row.currencyKey) || row.currencyKey),
    }));
    return {
      currencyBreakdown,
      totalAmountRows: currencyBreakdown.map((row) => ({ currency: row.currency, amount: row.totalAmount })),
      totalPaidRows: currencyBreakdown.map((row) => ({ currency: row.currency, amount: row.totalPaid })),
      totalRemainingRows: currencyBreakdown.map((row) => ({ currency: row.currency, amount: row.totalRemaining })),
    };
  };

  const customerAgentSummary = useMemo(
    () => buildAgentSummary(customers),
    [customers, currencies]
  );

  const supplierAgentSummary = useMemo(
    () => buildAgentSummary(suppliers),
    [suppliers, currencies]
  );

  const barData = useMemo(
    () => buildComparisonChartData(customerAgentSummary.totalAmountRows, supplierAgentSummary.totalAmountRows),
    [customerAgentSummary.totalAmountRows, supplierAgentSummary.totalAmountRows]
  );

  const paymentData = useMemo(
    () => buildPaymentComparisonChartData(customerAgentSummary.totalPaidRows, supplierAgentSummary.totalPaidRows),
    [customerAgentSummary.totalPaidRows, supplierAgentSummary.totalPaidRows]
  );

  const profitLossChartData = useMemo(() => {
    const rows = Array.isArray(data?.total_sale_profit_loss)
      ? data.total_sale_profit_loss
      : [];
    const map = new Map();
    rows.forEach((item) => {
      const currency = toSafeCurrencyLabel(item?.currency);
      const amount = parseFiniteNumber(item?.amount);
      const prev = map.get(currency) || 0;
      map.set(currency, prev + amount);
    });
    if (!map.has("AFG")) map.set("AFG", 0);
    if (!map.has("USD")) map.set("USD", 0);
    return Array.from(map.entries()).map(([currency, amount]) => ({
      currency,
      profit: amount > 0 ? amount : 0,
      loss: amount < 0 ? Math.abs(amount) : 0,
    })).filter((row) => parseFiniteNumber(row.profit) > 0 || parseFiniteNumber(row.loss) > 0);
  }, [data]);

  const purchaseByCurrencyChartData = useMemo(
    () => buildPieChartData(supplierAgentSummary.totalAmountRows),
    [supplierAgentSummary.totalAmountRows]
  );

  const salesByCurrencyChartData = useMemo(
    () => buildPieChartData(customerAgentSummary.totalAmountRows),
    [customerAgentSummary.totalAmountRows]
  );


  const renderSummaryCard = (title, items = [], subtitle = null, isCalculated = false, incomes = [], outcomes = [], expenses = [], payrolls = [], isProfitLoss = false, options = {}) => {
    const hideCurrency = Boolean(options?.hideCurrency);
    const decimalPlaces = Number.isFinite(options?.decimalPlaces) ? options.decimalPlaces : 2;
    let displayItems = Array.isArray(items) ? items : [];
    
    // If this is a calculated field, calculate it properly
    if (isCalculated) {
      const incomeMap = {};
      const outcomeMap = {};
      const expenseMap = {};
      const payrollMap = {};
      
      (incomes || []).forEach(item => {
        const currency = item.currency || '---';
        incomeMap[currency] = (incomeMap[currency] || 0) + parseFiniteNumber(item.amount);
      });
      
      (outcomes || []).forEach(item => {
        const currency = item.currency || '---';
        outcomeMap[currency] = (outcomeMap[currency] || 0) + parseFiniteNumber(item.amount);
      });
      
      (expenses || []).forEach(item => {
        const currency = item.currency || '---';
        expenseMap[currency] = (expenseMap[currency] || 0) + parseFiniteNumber(item.amount);
      });
      
      (payrolls || []).forEach(item => {
        const currency = item.currency || '---';
        payrollMap[currency] = (payrollMap[currency] || 0) + parseFiniteNumber(item.amount);
      });
      
      const allCurrencies = [...new Set([
        ...Object.keys(incomeMap), 
        ...Object.keys(outcomeMap),
        ...Object.keys(expenseMap),
        ...Object.keys(payrollMap)
      ])];
      
      displayItems = allCurrencies.map(currency => ({
        currency,
        amount: (incomeMap[currency] || 0) + (outcomeMap[currency] || 0) - (expenseMap[currency] || 0) - (payrollMap[currency] || 0)
      }));
    }
    
      const withPrimaryCurrencies = (() => {
      const map = new Map();
      (displayItems || []).forEach((item) => {
        const currency = toSafeCurrencyLabel(item?.currency);
        const amount = parseFiniteNumber(item?.amount);
        map.set(currency, (map.get(currency) || 0) + amount);
      });
      return Array.from(map.entries()).map(([currency, amount]) => ({ currency, amount }));
    })();
    const profitLossRows = (() => {
      if (!isProfitLoss) return [];
      const map = new Map();
      (displayItems || []).forEach((item) => {
        const currency = toSafeCurrencyLabel(item?.currency);
        const amount = parseFiniteNumber(item?.amount);
        const prev = map.get(currency) || { profit: 0, loss: 0 };
        if (amount >= 0) {
          prev.profit += amount;
        } else {
          prev.loss += Math.abs(amount);
        }
        map.set(currency, prev);
      });
      return Array.from(map.entries()).map(([currency, values]) => ({
        currency,
        profit: values.profit,
        loss: values.loss,
      }));
    })();

    return (
      <Card
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 2.5,
          background: panelBg,
          border: panelBorder,
          boxShadow: panelShadow,
          minHeight: 150,
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: isDarkMode ? "0 14px 34px rgba(0,0,0,0.52)" : "0 10px 26px rgba(0, 0, 0, 0.12)",
            borderColor: panelHoverBorder,
            transform: "translateY(-2px)",
          },
          direction: themeDirection,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ color: panelSubtext }} fontWeight={600}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: panelSubtext }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: "#355d31",
              borderRadius: "50%",
              p: 0.7,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AttachMoneyIcon fontSize="small" sx={{ color: "#fff" }} />
          </Box>
        </Box>
        <Box sx={{ mt: 1 }}>
          {isLoading ? (
            <CircularProgress size={24} />
          ) : isProfitLoss && profitLossRows.length > 0 ? (
            profitLossRows.map((row, idx) => (
              <Box key={idx} sx={{ mb: idx < profitLossRows.length - 1 ? 1.5 : 0 }}>
                <Typography variant="caption" sx={{ color: "success.main", fontWeight: 700, display: "block" }}>
                  Profit
                </Typography>
                <Typography variant="h6" fontWeight={700} sx={{ color: "success.main" }}>
                  +{formatDashboardAmount(row.profit, decimalPlaces)} {hideCurrency ? "" : String(row.currency || "---")}
                </Typography>
                <Typography variant="caption" sx={{ color: "error.main", fontWeight: 700, display: "block", mt: 0.5 }}>
                  Loss
                </Typography>
                <Typography variant="h6" fontWeight={700} sx={{ color: "error.main" }}>
                  -{formatDashboardAmount(row.loss, decimalPlaces)} {hideCurrency ? "" : String(row.currency || "---")}
                </Typography>
              </Box>
            ))
          ) : withPrimaryCurrencies && withPrimaryCurrencies.length > 0 ? (
            withPrimaryCurrencies.map((item, idx) => {
              const amount = parseFiniteNumber(item.amount);
              const isNegative = amount < 0;
              const isZero = amount === 0;
              const statusLabel = amount > 0 ? "Profit" : amount < 0 ? "Loss" : "Break-even";
              const statusColor = amount > 0 ? "success.main" : amount < 0 ? "error.main" : "text.secondary";
              return (
                <Box key={idx} sx={{ mb: idx < withPrimaryCurrencies.length - 1 ? 1 : 0 }}>
                  {isProfitLoss && (
                    <Typography variant="caption" sx={{ color: statusColor, fontWeight: 700, display: "block" }}>
                      {statusLabel}
                    </Typography>
                  )}
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{
                      mt: 1,
                      fontSize: "1.7rem",
                      color: isZero
                        ? (isDarkMode ? "rgba(255,255,255,0.45)" : "#9ca3af")
                        : (isCalculated || isProfitLoss)
                        ? (isNegative ? "#ef4444" : "#86efac")
                        : panelText,
                    }}
                  >
                    {(isCalculated || isProfitLoss) && !isNegative && amount !== 0 ? "+" : ""}
                    {formatDashboardAmount(amount, decimalPlaces)}
                    {!hideCurrency && String(item.currency || "").trim() ? ` ${String(item.currency).trim()}` : ""}
                  </Typography>
                </Box>
              );
            })
          ) : (
            <Typography variant="h5" fontWeight={700} sx={{ color: "#6b7280" }}>
              00 .0
            </Typography>
          )}
        </Box>
      </Card>
    );
  };

  if (isError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Error loading dashboard: {error?.message || "Unknown error"}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 4,
        mt: 2,
        minHeight: "calc(100vh - 64px)",
        direction: themeDirection,
        background: isDarkMode ? "#121212" : "transparent",
      }}
    >
      <Grid container spacing={2} alignItems="flex-start" justifyContent="space-between" mb={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="h4" sx={{ color: isDarkMode ? "#f5f5f5" : "text.primary", letterSpacing: 0.3 }}>
            {t("dashboard.dashboard")}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "220px 220px 160px 160px 100px",
              },
              justifyContent: "end",
              alignItems: "start",
              gap: 1.5,
            }}
          >
            <FormControl
              sx={{
                minWidth: 220,
                width: "100%",
                "& .MuiInputLabel-root": { color: isDarkMode ? "rgba(255,255,255,0.65)" : undefined },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: isDarkMode ? "#171717" : "#fff",
                  color: isDarkMode ? "#f3f4f6" : undefined,
                  "& fieldset": {
                    borderColor: isDarkMode ? "rgba(255,255,255,0.16)" : undefined,
                  },
                  "&:hover fieldset": {
                    borderColor: isDarkMode ? "rgba(255,255,255,0.28)" : undefined,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#355d31",
                  },
                },
                "& .MuiSelect-icon": {
                  color: isDarkMode ? "rgba(255,255,255,0.75)" : undefined,
                },
              }}
            >
              <InputLabel id="dashboard-supplier-label">{t("dashboard.supplierFilter", "Supplier")}</InputLabel>
              <Select
                labelId="dashboard-supplier-label"
                value={selectedSupplier}
                label={t("dashboard.supplierFilter", "Supplier")}
                onChange={(e) => {
                  setSelectedSupplier(e.target.value);
                }}
              >
                <MenuItem value="">{t("dashboard.allSuppliers", "All Suppliers")}</MenuItem>
                {suppliers.map((supplier) => (
                  <MenuItem key={supplier.id} value={String(supplier.id)}>
                    {supplier.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              sx={{
                minWidth: 220,
                width: "100%",
                "& .MuiInputLabel-root": { color: isDarkMode ? "rgba(255,255,255,0.65)" : undefined },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: isDarkMode ? "#171717" : "#fff",
                  color: isDarkMode ? "#f3f4f6" : undefined,
                  "& fieldset": {
                    borderColor: isDarkMode ? "rgba(255,255,255,0.16)" : undefined,
                  },
                  "&:hover fieldset": {
                    borderColor: isDarkMode ? "rgba(255,255,255,0.28)" : undefined,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#355d31",
                  },
                },
                "& .MuiSelect-icon": {
                  color: isDarkMode ? "rgba(255,255,255,0.75)" : undefined,
                },
              }}
            >
              <InputLabel id="dashboard-customer-label">{t("dashboard.customerFilter", "Customer")}</InputLabel>
              <Select
                labelId="dashboard-customer-label"
                value={selectedCustomer}
                label={t("dashboard.customerFilter", "Customer")}
                onChange={(e) => setSelectedCustomer(e.target.value)}
              >
                <MenuItem value="">{t("dashboard.allCustomers", "All Customers")}</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={String(customer.id)}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start date"
                value={startDate}
                onChange={setStartDate}
                slotProps={{
                  textField: {
                    InputLabelProps: { shrink: true },
                    inputProps: { style: { direction: "ltr" } },
                    sx: {
                      width: "100%",
                      "& .MuiInputLabel-root": { color: isDarkMode ? "rgba(255,255,255,0.65)" : undefined },
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: isDarkMode ? "#171717" : "#fff",
                        color: isDarkMode ? "#f3f4f6" : undefined,
                        "& fieldset": {
                          borderColor: isDarkMode ? "rgba(255,255,255,0.16)" : undefined,
                        },
                        "&:hover fieldset": {
                          borderColor: isDarkMode ? "rgba(255,255,255,0.28)" : undefined,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#355d31",
                        },
                      },
                    },
                  },
                }}
                sx={{ direction: "ltr" }}
              />
              <DatePicker
                label="End date"
                value={endDate}
                onChange={setEndDate}
                slotProps={{
                  textField: {
                    InputLabelProps: { shrink: true },
                    inputProps: { style: { direction: "ltr" } },
                    sx: {
                      width: "100%",
                      "& .MuiInputLabel-root": { color: isDarkMode ? "rgba(255,255,255,0.65)" : undefined },
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: isDarkMode ? "#171717" : "#fff",
                        color: isDarkMode ? "#f3f4f6" : undefined,
                        "& fieldset": {
                          borderColor: isDarkMode ? "rgba(255,255,255,0.16)" : undefined,
                        },
                        "&:hover fieldset": {
                          borderColor: isDarkMode ? "rgba(255,255,255,0.28)" : undefined,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#355d31",
                        },
                      },
                    },
                  },
                }}
                sx={{ direction: "ltr" }}
              />
            </LocalizationProvider>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
                setSelectedSupplier("");
                setSelectedCustomer("");
              }}
              sx={{ width: "100%", height: 56, backgroundColor: "#355d31", "&:hover": { backgroundColor: "#274923" } }}
            >
              {t("dashboard.clearBtn")}
            </Button>
          </Box>
          {dateError && <Typography sx={{ color: "error.main", mt: 1 }}>{dateError}</Typography>}
        </Grid>
      </Grid>


      {/* Main Summary */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(1, 1fr)", md: "repeat(3, 1fr)" },
          gap: 2,
        }}
      >
        {renderSummaryCard(t("dashboard.totalSales", "Total Sales"), customerAgentSummary.totalAmountRows, t("dashboard.totalSalesInvoiceAmount"), false, [], [], [], [], false, { decimalPlaces: 0 })}
        {renderSummaryCard(t("dashboard.customerCollections", "Customer Collections"), customerAgentSummary.totalPaidRows, t("dashboard.amountReceivedFromCustomers"), false, [], [], [], [], false, { decimalPlaces: 0 })}
        {renderSummaryCard(t("dashboard.customerReceivables", "Customer Receivables"), customerAgentSummary.totalRemainingRows, t("dashboard.remainingBalanceToReceive"), false, [], [], [], [], false, { decimalPlaces: 0 })}
        {renderSummaryCard(t("dashboard.supplierPayables", "Supplier Payables"), supplierAgentSummary.totalAmountRows, t("dashboard.totalPurchaseInvoiceAmount"), false, [], [], [], [], false, { decimalPlaces: 0 })}
        {renderSummaryCard(t("dashboard.supplierPayments", "Supplier Payments"), supplierAgentSummary.totalPaidRows, t("dashboard.amountPaidToSuppliers"), false, [], [], [], [], false, { decimalPlaces: 0 })}
        {renderSummaryCard(t("dashboard.outstandingSupplierBalance", "Outstanding Supplier Balance"), supplierAgentSummary.totalRemainingRows, t("dashboard.remainingBalanceToPay"), false, [], [], [], [], false, { decimalPlaces: 0 })}
      </Box>

      {/* Expenses & Other */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(1, 1fr)", md: "repeat(3, 1fr)" },
          gap: 2,
        }}
      >

      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(1, 1fr)", lg: "repeat(4, 1fr)" }, gap: 3 }}>
        {[
          { title: t("dashboard.PurchaseByCurrency"), data: purchaseByCurrencyChartData },
          { title: t("dashboard.SalesByCurrency"), data: salesByCurrencyChartData },
          { title: t("dashboard.SaleReturnByCurrency"), data: pieDataFrom("total_returns") },
          { title: t("dashboard.PurchaseReturnByCurrency"), data: pieDataFrom("total_purchase_returns") },
        ].map(({ title, data: chartData }) => (
          <Card
            key={title}
            elevation={3}
            sx={{
              p: 2,
              borderRadius: 3,
              background: panelBg,
              border: panelBorder,
              boxShadow: panelShadow,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} mb={2} sx={{ color: panelText }}>
              {title}
            </Typography>
            <Box sx={{ height: 260 }}>
              {chartData && chartData.length > 0 ? (
                <ResponsivePie
                  data={chartData}
                  margin={{ top: 10, right: 20, bottom: 40, left: 20 }}
                  innerRadius={0.5}
                  padAngle={0.7}
                  cornerRadius={3}
                  colors={COLORS}
                  activeOuterRadiusOffset={8}
                  borderWidth={1}
                  borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                  legends={[{ anchor: "bottom", direction: "row", translateY: 32, itemWidth: 80, itemHeight: 14 }]}
                  theme={{
                    text: { fill: isDarkMode ? "#e5e7eb" : "#374151" },
                    axis: {
                      ticks: { text: { fill: isDarkMode ? "#e5e7eb" : "#374151" } },
                      legend: { text: { fill: isDarkMode ? "#e5e7eb" : "#374151" } },
                    },
                    legends: { text: { fill: isDarkMode ? "#e5e7eb" : "#374151" } },
                    tooltip: {
                      container: {
                        background: isDarkMode ? "#111827" : "#ffffff",
                        color: isDarkMode ? "#f9fafb" : "#111827",
                      },
                    },
                  }}
                />
              ) : (
                <Box sx={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
                  <Typography
                    sx={{
                      color: panelSubtext,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 999,
                      backgroundColor: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(53,93,49,0.06)",
                      fontWeight: 600,
                      userSelect: "none",
                    }}
                  >
                    {EMPTY_CHART_TEXT}
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>
        ))}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" }, gap: 3 }}>
        <Card
          elevation={3}
          sx={{
            p: 2,
            borderRadius: 3,
            background: panelBg,
            border: panelBorder,
            boxShadow: panelShadow,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} mb={2} sx={{ color: panelText }}>
            {t("dashboard.SalesVsPurchases")}
          </Typography>
          <Box sx={{ height: 260 }}>
            {barData && barData.length > 0 ? (
              <ResponsiveBar
                data={barData}
                keys={["sales", "purchases"]}
                indexBy="currency"
                margin={{ top: 10, right: 20, bottom: 40, left: 60 }}
                padding={0.3}
                axisTop={null}
                axisRight={null}
                axisBottom={{ tickSize: 0, tickPadding: 12 }}
                axisLeft={{ tickSize: 0, tickPadding: 12 }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                legends={[{ anchor: "bottom", direction: "row", translateY: 32 }]}
                colors={{ scheme: "nivo" }}
                theme={{
                  text: { fill: isDarkMode ? "#e5e7eb" : "#374151" },
                  axis: {
                    ticks: { text: { fill: isDarkMode ? "#e5e7eb" : "#374151" } },
                    legend: { text: { fill: isDarkMode ? "#e5e7eb" : "#374151" } },
                  },
                  legends: { text: { fill: isDarkMode ? "#e5e7eb" : "#374151" } },
                  grid: { line: { stroke: isDarkMode ? "rgba(255,255,255,0.08)" : "#e5e7eb" } },
                  tooltip: {
                    container: {
                      background: isDarkMode ? "#111827" : "#ffffff",
                      color: isDarkMode ? "#f9fafb" : "#111827",
                    },
                  },
                }}
              />
            ) : (
              <Box sx={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
                <Typography
                  sx={{
                    color: panelSubtext,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 999,
                    backgroundColor: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(53,93,49,0.06)",
                    fontWeight: 600,
                    userSelect: "none",
                  }}
                >
                  {EMPTY_CHART_TEXT}
                </Typography>
              </Box>
            )}
          </Box>
        </Card>

        <Card
          elevation={3}
          sx={{
            p: 2,
            borderRadius: 3,
            background: panelBg,
            border: panelBorder,
            boxShadow: panelShadow,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} mb={2} sx={{ color: panelText }}>
            {t("dashboard.salesPaymentVsPurchasePayment")}
          </Typography>
          <Box sx={{ height: 260 }}>
            {paymentData && paymentData.length > 0 ? (
              <ResponsiveBar
                data={paymentData}
                keys={["sales_paid", "purchases_paid"]}
                indexBy="currency"
                margin={{ top: 10, right: 20, bottom: 40, left: 60 }}
                padding={0.3}
                axisTop={null}
                axisRight={null}
                axisBottom={{ tickSize: 0, tickPadding: 12 }}
                axisLeft={{ tickSize: 0, tickPadding: 12 }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                legends={[{ anchor: "bottom", direction: "row", translateY: 32 }]}
                colors={["#4CAF50", "#FF9800"]}
                theme={{
                  text: { fill: isDarkMode ? "#e5e7eb" : "#374151" },
                  axis: {
                    ticks: { text: { fill: isDarkMode ? "#e5e7eb" : "#374151" } },
                    legend: { text: { fill: isDarkMode ? "#e5e7eb" : "#374151" } },
                  },
                  legends: { text: { fill: isDarkMode ? "#e5e7eb" : "#374151" } },
                  grid: { line: { stroke: isDarkMode ? "rgba(255,255,255,0.08)" : "#e5e7eb" } },
                  tooltip: {
                    container: {
                      background: isDarkMode ? "#111827" : "#ffffff",
                      color: isDarkMode ? "#f9fafb" : "#111827",
                    },
                  },
                }}
              />
            ) : (
              <Box sx={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
                <Typography
                  sx={{
                    color: panelSubtext,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 999,
                    backgroundColor: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(53,93,49,0.06)",
                    fontWeight: 600,
                    userSelect: "none",
                  }}
                >
                  {EMPTY_CHART_TEXT}
                </Typography>
              </Box>
            )}
          </Box>
        </Card>
      </Box>

      <Card
        elevation={3}
        sx={{
          p: 2,
          borderRadius: 3,
          background: panelBg,
          border: panelBorder,
          boxShadow: panelShadow,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} mb={2} sx={{ color: panelText }}>
          {t("dashboard.profitLossState", "Profit & Loss State")}
        </Typography>
        <Box sx={{ height: 300 }}>
          {profitLossChartData && profitLossChartData.length > 0 ? (
            <ResponsiveBar
              data={profitLossChartData}
              keys={["profit", "loss"]}
              indexBy="currency"
              margin={{ top: 10, right: 20, bottom: 40, left: 60 }}
              padding={0.3}
              axisTop={null}
              axisRight={null}
              axisBottom={{ tickSize: 0, tickPadding: 12 }}
              axisLeft={{ tickSize: 0, tickPadding: 12 }}
              labelSkipWidth={12}
              labelSkipHeight={12}
              legends={[{ anchor: "bottom", direction: "row", translateY: 32 }]}
              colors={["#2e7d32", "#d32f2f"]}
              theme={{
                text: { fill: isDarkMode ? "#e5e7eb" : "#374151" },
                axis: {
                  ticks: { text: { fill: isDarkMode ? "#e5e7eb" : "#374151" } },
                  legend: { text: { fill: isDarkMode ? "#e5e7eb" : "#374151" } },
                },
                legends: { text: { fill: isDarkMode ? "#e5e7eb" : "#374151" } },
                grid: { line: { stroke: isDarkMode ? "rgba(255,255,255,0.08)" : "#e5e7eb" } },
                tooltip: {
                  container: {
                    background: isDarkMode ? "#111827" : "#ffffff",
                    color: isDarkMode ? "#f9fafb" : "#111827",
                  },
                },
              }}
            />
          ) : (
            <Box sx={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
              <Typography
                sx={{
                  color: panelSubtext,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 999,
                  backgroundColor: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(53,93,49,0.06)",
                  fontWeight: 600,
                  userSelect: "none",
                }}
              >
                {EMPTY_CHART_TEXT}
              </Typography>
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default Dashboard;



